export interface ConversationServiceOptions {
  apiUrl: string
  /** Bearer token for authorization */
  token: string
}

export class ConversationServiceError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly apiMessage?: string,
  ) {
    super(message)
    this.name = 'ConversationServiceError'
  }
}

export type ConversationStatus = 'open' | 'closed'

export type ConversationType =
  | 'other'
  | 'application'
  | 'deceasedCustomer'
  | 'interestCostDistribution'
  | 'disbursementControl'
  | 'housingAssociationNotice'
  | 'excessMortgage'
  | 'nonAmortizationValueChange'
  | 'amortizationBaseValueChange'
  | 'pledgingCorrection'
  | 'borrowerChange'
  | 'ledgerEntryRemoval'
  | 'forbearance'
  | 'customerLoanRedemption'
  | 'disbursement'
  | 'loanRedemption'
  | 'preAccessControl'
  | 'amortizationRelief'
  | 'extraAmortization'
  | 'leaseholdBuyout'
  | 'amortizationStatement'
  | 'amortizationIncreaseAboveLimit'
  | 'fixedInterestBreakCost'
  | 'generalLoanQuestions'

export type StaticSubjectConversationType = Exclude<ConversationType, 'other' | 'application'>

export const CONVERSATION_STATIC_SUBJECTS_BY_TYPE: Record<StaticSubjectConversationType, string> = {
  deceasedCustomer: 'Avliden kund',
  interestCostDistribution: 'Fördela räntekostnaderna till deklarationen',
  disbursementControl: 'Utbetalningskontroll',
  housingAssociationNotice: 'Underrättelse från BRF',
  excessMortgage: 'Överhypotek',
  nonAmortizationValueChange: 'Ändring av marknadsvärde ej amorteringsgrundande',
  amortizationBaseValueChange: 'Ändring av amorteringsgrundande värde',
  pledgingCorrection: 'Rättelse av felaktig pantsättning',
  borrowerChange: 'Tillägg eller borttag av låntagare',
  ledgerEntryRemoval: 'Borttag av poster från reskontran',
  forbearance: 'Forbearance – markering och rapportering',
  customerLoanRedemption: 'Lösen bolån – kund löser själv',
  disbursement: 'Utbetalning',
  loanRedemption: 'Lösen av HPL eller ÖBL',
  preAccessControl: 'Kontroll inför tillträde',
  amortizationRelief: 'Amorteringslättnad',
  extraAmortization: 'Extraamortering',
  leaseholdBuyout: 'Friköp av tomträtt',
  amortizationStatement: 'Amorteringsunderlag',
  amortizationIncreaseAboveLimit: 'Höj amortering till över 3%',
  fixedInterestBreakCost: 'Lösen av bunden ränta',
  generalLoanQuestions: 'Lånefrågor',
}

export const CONVERSATION_DISPLAY_LABELS_BY_TYPE: Record<ConversationType, string> = {
  application: 'Ansökan',
  other: 'Övrigt',
  ...CONVERSATION_STATIC_SUBJECTS_BY_TYPE,
}

export type ConversationMessageAuthor = {
  displayName: string
  organisationName: string
}

export type ExternalParticipantInfo = {
  externalRoutingPath?: string
  displayName?: string
}

export type ConversationMessage = {
  id: string
  inReplyToMessageId?: string
  message: string
  readAt: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  author: ConversationMessageAuthor
}

export type ConversationListItem = {
  id: string
  applicationId: string | null
  debtId: string | null
  type: ConversationType
  subject: string
  status: ConversationStatus
  createdAt: string
  updatedAt: string
  hasUnreadMessages: boolean
  latestMessage: ConversationMessage | null
}

export type Conversation = {
  id: string
  applicationId: string | null
  debtId: string | null
  type: ConversationType
  subject: string
  status: ConversationStatus
  createdAt: string
  updatedAt: string
  hasUnreadMessages: boolean
  messages: ConversationMessage[]
}

type ConversationTargetPayload = { applicationId: string; debtId?: never } | { debtId: string; applicationId?: never }

type CreateOtherConversationPayload = {
  type: 'other'
  subject: string
}

type CreateApplicationConversationPayload = {
  type: 'application'
  subject?: string
}

type CreateStaticSubjectConversationPayload = {
  type: StaticSubjectConversationType
  subject?: never
}

export type CreateConversationPayload = ConversationTargetPayload &
  (CreateOtherConversationPayload | CreateApplicationConversationPayload | CreateStaticSubjectConversationPayload)

export type CreateConversationMessagePayload = {
  message: string
  inReplyToMessageId?: string
  externalParticipantInfo?: ExternalParticipantInfo
}

export type UpdateConversationMessagePayload =
  | {
      message: string
    }
  | {
      markAsRead: boolean
    }

export type ConversationListFilters = {
  status?: ConversationStatus
  onlyUnread?: true
}
